import { useCallback, useEffect, useMemo } from 'react';

import { useFormContext } from '@/components/react-hook-form';
import {
  DiagramDispositionsFormRecipient,
  useOptionalDispositiveDiagramSyncContext,
} from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import {
  enrichRecipientForDiagram,
  RecipientDisplayMetadata,
  useBootstrapDiagramSelectedNode,
} from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.helpers';
import {
  DispositiveProvisionsFormRecipient,
  RecipientKind,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { isPlaceholderRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/primaryClientDeathSection.utils';
import {
  BdgPrimaryRecipient,
  decideActiveBdgTier,
  getMissingBeneficiaryTier,
  isPrimaryTierUnavailable,
} from '@/modules/entityDiagram/bdgActiveTier';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { AfterDeath } from '@/types/schema';

import { BeneficiaryDesignationsFormShape } from '../BeneficiaryDesignationsForm.types';
import { useBeneficiaryDesignationRecipientOptions } from './useBeneficiaryDesignationRecipientOptions';

interface UseSyncBeneficiaryDesignationsDiagramArgs {
  /**
   * The entity whose recipients this form edits: `entityIdOverride ??
   * contextEntityId` from the form. For a non-root target this is the
   * clicked tile's id (not the diagram root), which keys the non-root
   * override. Passed in rather than read from `useEntityDetailsContext`,
   * which returns the diagram root and would mis-key the override.
   */
  entityId: string | null;
  primaryRecipients: DispositiveProvisionsFormRecipient[] | undefined;
  contingentRecipients: DispositiveProvisionsFormRecipient[] | undefined;
  /**
   * False when the BDG form is mounted for a non-root target (e.g., a
   * retirement tile clicked from a rev trust's DP diagram). In that case
   * the root diagram overlay must not be written — it would clobber the
   * root entity's preview — so we publish a `nonRootRecipientOverride`
   * keyed by `entityId` instead.
   */
  isEditingRootTarget: boolean;
  /**
   * True when the BDG form's source account is a retirement account. Gates
   * the recipient look-through to retirement sources only (T1-6171); when
   * false/omitted, account recipients keep prior always-available behavior,
   * matching the backend. Mirrors the gate in
   * `resolveEntityNormalizedDispositions`.
   */
  sourceIsRetirement?: boolean;
  /** Must match the recipient typeahead so draft entities carry diagram
   * metadata and aren't dropped from the live preview. */
  isOnHypotheticalWaterfall?: boolean;
}

/**
 * BDG-form analogue of `useSyncDiagram`: writes the form's primary /
 * contingent recipients into the diagram sync context as
 * `{firstDeath, secondDeath}` so the split-screen diagram previews
 * unsaved edits. Routing mirrors `resolveEntityNormalizedDispositions`.
 */
export function useSyncBeneficiaryDesignationsDiagram({
  entityId,
  primaryRecipients,
  contingentRecipients,
  isEditingRootTarget,
  sourceIsRetirement = false,
  isOnHypotheticalWaterfall = false,
}: UseSyncBeneficiaryDesignationsDiagramArgs) {
  const { formState } = useFormContext<BeneficiaryDesignationsFormShape>();
  const syncContext = useOptionalDispositiveDiagramSyncContext();
  const {
    setDiagramDispositionsFormState,
    setNonRootRecipientOverride,
    selectedNode,
    setSelectedNode,
    bdgDiagramSyncState,
  } = syncContext ?? {};
  const { primaryClients } = useHouseholdDetailsContext();
  // Key the options to the edited entity, not the diagram root: a non-root
  // form may legitimately pick the root as a recipient, and missing metadata
  // would silently drop it from the published override.
  const { metadataById, options } = useBeneficiaryDesignationRecipientOptions({
    entityIdOverride: entityId ?? undefined,
    isOnHypotheticalWaterfall,
  });

  const displayMetadataById = useMemo<
    Record<string, RecipientDisplayMetadata>
  >(() => {
    const displayNameById = Object.fromEntries(
      options.map((o) => [o.value, o.display])
    );
    return Object.fromEntries(
      Object.entries(metadataById).map(([id, meta]) => [
        id,
        {
          displayName: displayNameById[id] ?? id,
          entityKind: 'entityKind' in meta ? meta.entityKind : undefined,
          testamentaryEntityKind:
            'testamentaryEntityKind' in meta
              ? meta.testamentaryEntityKind
              : undefined,
          isCharitableOrganization:
            'isCharitableOrganization' in meta
              ? meta.isCharitableOrganization
              : undefined,
          inEstateStatus:
            'inEstateStatus' in meta ? meta.inEstateStatus : undefined,
        },
      ])
    );
  }, [metadataById, options]);

  // BDG accounts always render at AfterDeath.None (single-tier flow).
  useBootstrapDiagramSelectedNode({
    entityId,
    defaultAfterDeath: AfterDeath.None,
    selectedNode,
    setSelectedNode,
  });

  const enrich = useCallback(
    (r: DispositiveProvisionsFormRecipient) =>
      enrichRecipientForDiagram(r, displayMetadataById[r.recipientId ?? '']),
    [displayMetadataById]
  );

  useEffect(() => {
    if (!setDiagramDispositionsFormState) return;
    if (!setNonRootRecipientOverride) return;
    if (formState.isSubmitting) return;
    if (formState.isLoading) return;

    const enrichedPrimary = (primaryRecipients ?? [])
      .map(enrich)
      .filter((x): x is DiagramDispositionsFormRecipient => x !== null);
    const enrichedContingent = (contingentRecipients ?? [])
      .map(enrich)
      .filter((x): x is DiagramDispositionsFormRecipient => x !== null);

    const possiblePrimaryClientIds = primaryClients?.map((c) => c.id) ?? [];
    const primaryRecipientsForTierCheck =
      enrichedPrimary.map<BdgPrimaryRecipient>((r) => ({
        isSurvivingSpouseSentinel:
          r.recipientKind === RecipientKind.SurvivingSpouse,
        individualId:
          r.recipientKind === RecipientKind.Individual ? r.recipientId : null,
        lookThroughOwnerIds: sourceIsRetirement
          ? (metadataById[r.recipientId ?? '']?.lookThroughOwnerIds ?? null)
          : null,
      }));

    // The missing-beneficiary signal for the edited entity's tile warning
    // must come from the live form (not the server-resolved dispositions) so
    // it tracks unsaved edits, matching the inline tier text.
    const hasPrimaryRecipient = (primaryRecipients ?? []).some(
      (r) => !isPlaceholderRecipient(r)
    );
    const hasContingentRecipient = (contingentRecipients ?? []).some(
      (r) => !isPlaceholderRecipient(r)
    );

    if (!isEditingRootTarget) {
      // Non-root target: this entity renders as a downstream recipient of
      // another root entity, in the cascade's second-death column. Publish a
      // form-recipient-typed override keyed by this entity's id so its
      // unsaved edits cascade without clobbering the root's overlay. Mirror
      // `resolveEntityNormalizedDispositions`'s placedAt:First branch:
      // downstream BDGs are reached only once both primary clients are dead,
      // so the tier check runs against all primary clients deceased — which
      // also resolves the surviving-spouse sentinel deceased, so no
      // `bdgDiagramSyncState` is needed (it's never published when the root
      // is a DP entity). Matches `pickActiveBdgFormTier`'s downstream branch.
      if (entityId) {
        const deceasedForTierCheck = new Set(possiblePrimaryClientIds);
        const useContingent = isPrimaryTierUnavailable(
          primaryRecipientsForTierCheck,
          deceasedForTierCheck,
          null
        );
        const activeRecipients = useContingent
          ? enrichedContingent
          : enrichedPrimary;
        setNonRootRecipientOverride({
          entityId,
          firstDeath: [],
          secondDeath: activeRecipients,
          missingBeneficiaryTier: getMissingBeneficiaryTier({
            hasPrimaryRecipient,
            hasContingentRecipient,
            useContingent,
          }),
        });
      }
    } else {
      // Editing the root entity: the diagram root is a BDG entity, so wait
      // for it to publish trigger client + current selection.
      if (!bdgDiagramSyncState) return;

      // Clear any stale non-root override, then pick the active tier and
      // slot it where the cascade reads, mirroring
      // `resolveEntityNormalizedDispositions`.
      setNonRootRecipientOverride(null);

      const { triggeringClientIds, selectedPrimaryClientId, isSurvivorship } =
        bdgDiagramSyncState;
      const { useContingent, triggerFiresAtSecondDeath } = decideActiveBdgTier({
        primaryRecipientsForTierCheck,
        selectedPrimaryClientId,
        triggeringClientIds,
        isSurvivorship,
        possiblePrimaryClientIds,
      });
      const activeRecipients = useContingent
        ? enrichedContingent
        : enrichedPrimary;

      const bdgRootMissingBeneficiaryTier = getMissingBeneficiaryTier({
        hasPrimaryRecipient,
        hasContingentRecipient,
        useContingent,
      });

      setDiagramDispositionsFormState(
        triggerFiresAtSecondDeath
          ? {
              firstDeath: [],
              secondDeath: activeRecipients,
              bdgRootMissingBeneficiaryTier,
            }
          : {
              firstDeath: activeRecipients,
              secondDeath: [],
              bdgRootMissingBeneficiaryTier,
            }
      );
    }
  }, [
    bdgDiagramSyncState,
    contingentRecipients,
    enrich,
    entityId,
    formState.isLoading,
    formState.isSubmitting,
    isEditingRootTarget,
    metadataById,
    primaryClients,
    primaryRecipients,
    setDiagramDispositionsFormState,
    setNonRootRecipientOverride,
    sourceIsRetirement,
  ]);

  // Clear the non-root override only on unmount, so it stays frozen during
  // save/load (when the effect above early-returns) like the root overlay,
  // rather than vanishing from the diagram mid-submit. A switch to a
  // different target republishes via the effect above (root clears it; a new
  // non-root target overwrites it by id).
  useEffect(
    () => () => setNonRootRecipientOverride?.(null),
    [setNonRootRecipientOverride]
  );
}
